import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { venueRefreshApi, apiErrorMessage } from "@/api/client";

const ORDER = ["4", "5", "3", "2", "1", "0", "unrated"];
export const fhrsSummary = (by = {}) => {
  const keys = [...ORDER.filter((k) => by[k]), ...Object.keys(by).filter((k) => !ORDER.includes(k))];
  return keys.map((k) => (k === "unrated" ? `Unrated: ${by[k]}` : /^\d$/.test(k) ? `${k}★: ${by[k]}` : `${k}: ${by[k]}`)).join(", ") || "none";
};

export const passLine = (p) =>
  `${p.type === "rating" ? `FHRS ${p.rating}` : "All"}: ${p.pages} pg · ${p.records} rec · ${p.new} new${p.truncated ? " · truncated" : ""} · ${p.elapsed_ms} ms`;

export function RefreshResult({ result }) {
  if (!result) return null;
  return (
    <div data-testid="venue-refresh-result" className="mt-3 rounded-lg border border-teal-100 bg-teal-50/60 p-3 text-xs text-slate-700">
      <p><b>{result.records_fetched}</b> record(s) across <b>{result.pages_fetched}</b> page(s){result.discovery_truncated ? " (truncated)" : ""}</p>
      <p>{result.new} new · {result.updated} updated · radius {result.radius_km} km</p>
      <p data-testid="venue-refresh-fhrs">By FHRS: {fhrsSummary(result.by_fhrs)}</p>
      {result.passes?.length > 0 && (
        <details data-testid="venue-refresh-details" className="mt-1">
          <summary className="cursor-pointer font-semibold text-teal-800">Details{result.elapsed_ms != null ? ` · ${(result.elapsed_ms / 1000).toFixed(1)} s` : ""}</summary>
          <ul className="mt-1 space-y-0.5 font-mono text-[11px]">
            {result.passes.map((p, i) => <li key={i} data-testid={`venue-refresh-pass-${i}`}>{passLine(p)}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

export async function refreshVenues(body) {
  try {
    const { data } = await venueRefreshApi.refresh(body);
    toast.success(`Venues refreshed: ${data.new} new, ${data.updated} updated`);
    return data;
  } catch (e) {
    toast.error(apiErrorMessage(e, "Could not refresh venues"));
    return null;
  }
}

export function RefreshVenuesForm() {
  const [postcode, setPostcode] = useState("");
  const [radius, setRadius] = useState(3);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!postcode.trim() || busy) return;
    setBusy(true);
    const out = await refreshVenues({ postcode: postcode.trim(), radius_km: Number(radius) });
    if (out) setResult(out);
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="venue-refresh-form" className="mb-4 rounded-xl border border-slate-200 bg-white p-4">
      <h2 className="font-heading text-sm font-bold text-slate-800">Refresh area venues</h2>
      <p className="mt-0.5 text-xs text-slate-400">Re-fetches FSA venues around a postcode into your venue cache. Updates only: nothing is deleted and no CRM records are created.</p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs font-semibold text-slate-600">Postcode
          <input data-testid="venue-refresh-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())}
            placeholder="B49 5AA" maxLength={10} className="mt-1 block min-h-[44px] w-36 rounded-lg border border-slate-200 px-3 text-sm" />
        </label>
        <label className="text-xs font-semibold text-slate-600">Radius (km)
          <input data-testid="venue-refresh-radius" type="number" min={0.5} max={15} step={0.5} value={radius}
            onChange={(e) => setRadius(Math.min(15, Math.max(0.5, Number(e.target.value) || 3)))}
            className="mt-1 block min-h-[44px] w-24 rounded-lg border border-slate-200 px-3 text-sm" />
        </label>
        <button type="submit" data-testid="venue-refresh-submit" disabled={busy || !postcode.trim()}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-4 text-sm font-semibold text-white disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />{busy ? "Refreshing…" : "Refresh venues"}
        </button>
      </div>
      <RefreshResult result={result} />
    </form>
  );
}
